import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../assets/logoBw.png';
import './Preloader.css';

const ROLES = ['DEVOPS ENGINEER', 'DATA ENGINEER', 'DATA ANALYST', 'FULL STACK DEVELOPER'];
const GREETINGS = ['WELCOME', 'BONJOUR'];

function PreloaderScene({ stage, percent, roleIndex, greetingIndex }) {
  return (
    <div className="preloader-scene">
      <div className="preloader-topbar">
        <img src={logo} alt="Santhosh Dayakar" className="preloader-logo" />
        <div className="preloader-eq" aria-hidden="true">
          <span className="preloader-eq-dot" />
          <span className="preloader-eq-bar" />
          <span className="preloader-eq-bar" />
          <span className="preloader-eq-bar" />
        </div>
      </div>

      <div className="preloader-center">
        <AnimatePresence mode="wait">
          {stage === 'loading' && (
            <motion.div
              key="loading"
              className="preloader-stage"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
            >
              <span className="preloader-name">SANTHOSH DAYAKAR</span>
              <AnimatePresence mode="wait">
                <motion.span
                  key={roleIndex}
                  className="preloader-role"
                  initial={{ y: 14, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -14, opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  {ROLES[roleIndex]}
                </motion.span>
              </AnimatePresence>

              <div className="preloader-pill">
                <svg className="preloader-ring" viewBox="0 0 280 64" preserveAspectRatio="none">
                  <rect
                    x="1"
                    y="1"
                    width="278"
                    height="62"
                    rx="31"
                    pathLength="100"
                    strokeDasharray="100"
                    strokeDashoffset={100 - percent}
                  />
                </svg>
                <span className="preloader-pill-label">LOADING</span>
                <span className="preloader-pill-percent">
                  {percent}%<span className="preloader-cursor" />
                </span>
              </div>
            </motion.div>
          )}

          {stage === 'greeting' && (
            <motion.div
              key="greeting"
              className="preloader-stage"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <AnimatePresence mode="wait">
                <motion.span
                  key={greetingIndex}
                  className="preloader-greeting"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.05 }}
                  transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                >
                  {GREETINGS[greetingIndex]}
                </motion.span>
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function Preloader({ onDone }) {
  const [percent, setPercent] = useState(0);
  const [stage, setStage] = useState('loading');
  const [roleIndex, setRoleIndex] = useState(0);
  const [greetingIndex, setGreetingIndex] = useState(0);
  const [curtain, setCurtain] = useState(false);
  const doneRef = useRef(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';

    let raf;
    const start = performance.now();
    const duration = 2600;

    const tick = (now) => {
      const elapsed = now - start;
      const t = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - t, 2.2);
      setPercent(Math.round(eased * 100));

      if (t < 1) {
        raf = requestAnimationFrame(tick);
      } else if (!doneRef.current) {
        doneRef.current = true;
        setStage('greeting');
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    if (stage !== 'loading') return;
    const t = setInterval(() => {
      setRoleIndex((i) => (i + 1) % ROLES.length);
    }, 650);
    return () => clearInterval(t);
  }, [stage]);

  useEffect(() => {
    if (stage !== 'greeting') return;
    const t1 = setTimeout(() => setGreetingIndex(1), 500);
    const t2 = setTimeout(() => setCurtain(true), 1000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [stage]);

  useEffect(() => {
    if (!curtain) return;
    const t = setTimeout(() => {
      document.body.style.overflow = '';
      onDone();
    }, 950);
    return () => clearTimeout(t);
  }, [curtain, onDone]);

  return (
    <div className="preloader-root" style={{ pointerEvents: curtain ? 'none' : 'auto' }}>
      <motion.div
        className="preloader-half preloader-half-top"
        animate={curtain ? { y: '-100%' } : { y: '0%' }}
        transition={{ duration: 0.85, ease: [0.76, 0, 0.24, 1] }}
      >
        <div className="preloader-half-inner preloader-half-inner-top">
          <PreloaderScene stage={stage} percent={percent} roleIndex={roleIndex} greetingIndex={greetingIndex} />
        </div>
      </motion.div>

      <motion.div
        className="preloader-half preloader-half-bottom"
        animate={curtain ? { y: '100%' } : { y: '0%' }}
        transition={{ duration: 0.85, ease: [0.76, 0, 0.24, 1] }}
      >
        <div className="preloader-half-inner preloader-half-inner-bottom">
          <PreloaderScene stage={stage} percent={percent} roleIndex={roleIndex} greetingIndex={greetingIndex} />
        </div>
      </motion.div>
    </div>
  );
}

export default Preloader;
